// API client para apps/expenses/ (Django backend).
// Endpoints: /api/v1/expenses/*
//
// Auth: JWT (Authorization: Bearer ...) propagado por el proxy Django.
// Multi-tenant: header X-Org-Id desde localStorage.org_id como fallback
// cuando el JWT no carga org_id.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  CategoryCreateIn,
  DashboardSummary,
  Expense,
  ExpenseCategory,
  ExpenseCreateIn,
  ExpenseListFilters,
  ExpenseListResponse,
  ReceiptUploadResponse,
} from './types';

const DJANGO_PROXY = '/django-api';

async function authHeaders(extra: Record<string, string> = {}): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class ExpensesApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ExpensesApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}, isJSON = true): Promise<T> {
  const headers = await authHeaders(
    isJSON ? { 'Content-Type': 'application/json' } : {},
  );
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new ExpensesApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function buildQS(filters: ExpenseListFilters): string {
  const p = new URLSearchParams();
  if (filters.date_from) p.set('date_from', filters.date_from);
  if (filters.date_to) p.set('date_to', filters.date_to);
  if (filters.category_id) p.set('category_id', filters.category_id);
  if (filters.project_id) p.set('project_id', filters.project_id);
  if (filters.vendor_id) p.set('vendor_id', filters.vendor_id);
  if (filters.status) p.set('status', filters.status);
  if (filters.limit !== undefined) p.set('limit', String(filters.limit));
  if (filters.offset !== undefined) p.set('offset', String(filters.offset));
  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

export const expensesApi = {
  // ── Categories ───────────────────────────────────
  listCategories: (activeOnly = true): Promise<ExpenseCategory[]> =>
    request<ExpenseCategory[]>(
      `/api/v1/expenses/categories/?active_only=${activeOnly}`,
    ),

  createCategory: (data: CategoryCreateIn): Promise<ExpenseCategory> =>
    request<ExpenseCategory>('/api/v1/expenses/categories/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ── Expenses CRUD ────────────────────────────────
  list: (filters: ExpenseListFilters = {}): Promise<ExpenseListResponse> =>
    request<ExpenseListResponse>(`/api/v1/expenses/${buildQS(filters)}`),

  get: (id: string): Promise<Expense> =>
    request<Expense>(`/api/v1/expenses/${id}/`),

  create: (data: ExpenseCreateIn): Promise<Expense> =>
    request<Expense>('/api/v1/expenses/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<ExpenseCreateIn>): Promise<Expense> =>
    request<Expense>(`/api/v1/expenses/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  remove: (id: string): Promise<void> =>
    request<void>(`/api/v1/expenses/${id}/`, { method: 'DELETE' }),

  // ── Dashboard ────────────────────────────────────
  dashboard: (): Promise<DashboardSummary> =>
    request<DashboardSummary>('/api/v1/expenses/dashboard/'),

  // ── Receipt upload (multipart/form-data) ─────────
  uploadReceipt: async (id: string, file: File): Promise<ReceiptUploadResponse> => {
    const headers = await authHeaders();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${DJANGO_PROXY}/api/v1/expenses/${id}/upload-receipt/`, {
      method: 'POST',
      headers,
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
      throw new ExpensesApiError(
        typeof detail === 'string' ? detail : JSON.stringify(detail),
        res.status,
      );
    }
    return res.json();
  },
};
